import { useTranslation } from 'react-i18next';

import * as monthUtils from '@actual-app/core/shared/months';

import { useLocale } from '#hooks/useLocale';

import { hayDosElegibles } from './escenas';
import type { EtiquetaAyuda, PasoAyuda, PreguntaAyuda } from './pasos';

function uno(selector: string): Element[] {
  const el = document.querySelector(selector);
  return el ? [el] : [];
}

/** Primera fila de cada tipo (`normal`, `sin-categoria`…) en la lista. */
function fila(tipo: string): Element | null {
  return document.querySelector(`[data-ayuda-fila="${tipo}"]`);
}

/** Fila que se usa para enseñar sus partes: una con categoría y «nuevo» si hay. */
function filaEjemplo(): Element | null {
  return (
    document.querySelector('[data-ayuda-fila="normal"][data-ayuda-nuevo]') ??
    fila('normal')
  );
}

function parte(f: Element | null, nombre: string): Element | null {
  return f?.querySelector(`[data-ayuda="${nombre}"]`) ?? null;
}

/**
 * La ayuda de Movimientos (maqueta aprobada por Vadik el 24-oct-2026; menú de
 * preguntas, 26-oct-2026). Cada paso busca en la pantalla de verdad lo que
 * enseña; si no está (no hay nada sin categoría, no hay pagos fijos…), se
 * salta.
 */
export function usePreguntasMovimientos(): PreguntaAyuda[] {
  const { t } = useTranslation();
  const locale = useLocale();
  const mes = monthUtils.format(monthUtils.currentMonth(), 'MMMM', locale);
  const hayNuevo = () =>
    !!filaEjemplo()?.querySelector('[data-testid="punto-nuevo"]');

  const todo: PasoAyuda[] = [
    {
      id: 'hola',
      escena: 'normal',
      texto: t(
        'Hi! This is Transactions: everything that comes in and goes out of all your accounts, grouped by day. The newest is at the top.',
      ),
      objetivo: () => uno('[data-ayuda="titulo-movimientos"]'),
    },
    {
      id: 'gastado',
      escena: 'normal',
      texto: t(
        'This is what you have spent so far in {{month}}. It is the same figure you see in Home and Reports.',
        { month: mes },
      ),
      objetivo: () => uno('[data-testid="movimientos-gastado-mes"]'),
    },
    {
      id: 'buscar',
      escena: 'normal',
      texto: t(
        'Looking for something? Type a shop, a category or a note and I will tell you how many transactions match and how much they add up to.',
      ),
      objetivo: () => uno('[data-testid="zona-buscador"]'),
    },
    {
      id: 'proximos',
      escena: 'normal',
      texto: t(
        '"Upcoming" holds the fixed payments that are still to come. Tap it to open or close the list.',
      ),
      objetivo: () => uno('[data-testid="cabecera-proximos"]'),
    },
    {
      id: 'fila',
      escena: 'normal',
      radio: 12,
      texto: t(
        'Each row is a transaction: where it was, which category and how much. Expenses carry a minus; income is green with a plus. Tap it to change anything.',
      ),
      objetivo: () => {
        const f = filaEjemplo();
        return f ? [f] : [];
      },
      etiquetas: () => {
        const f = filaEjemplo();
        const etiquetas: EtiquetaAyuda[] = [];
        for (const [nombre, texto, debajo] of [
          ['comercio', t('Payee'), false],
          ['categoria', t('Category'), true],
          ['importe', t('Amount'), false],
        ] as const) {
          const el = parte(f, nombre);
          if (el) {
            etiquetas.push({ el, texto, debajo });
          }
        }
        return etiquetas;
      },
    },
    {
      id: 'punto',
      escena: 'normal',
      radio: 10,
      disponible: hayNuevo,
      texto: t(
        'The green dot means "new": it came from the bank or a statement and you have not checked it yet. The green ✓: the bank has already confirmed it.',
      ),
      objetivo: () => {
        const f = filaEjemplo();
        return [
          f?.querySelector('[data-testid="punto-nuevo"]'),
          parte(f, 'confirmado'),
        ].filter((e): e is Element => !!e);
      },
    },
    {
      id: 'confirmado',
      escena: 'normal',
      radio: 10,
      disponible: () => !hayNuevo(),
      texto: t(
        'The green ✓ means the bank has already confirmed it; grey, not yet.',
      ),
      objetivo: () => {
        const c = parte(filaEjemplo(), 'confirmado');
        return c ? [c] : [];
      },
    },
    {
      id: 'sin-categoria',
      escena: 'normal',
      radio: 12,
      texto: t(
        '"Uncategorized", in amber: I do not know which category it goes to. Tap it and choose one; that way your Plan adds up.',
      ),
      objetivo: () => {
        const f = fila('sin-categoria');
        return f ? [f] : [];
      },
    },
    {
      id: 'brujula',
      escena: 'normal',
      radio: 30,
      toque: true,
      texto: t(
        'Now tap the compass, at the bottom right. On every screen it keeps what you can do there.',
      ),
      objetivo: () => uno('[data-testid="brujula-opciones"]'),
    },
    {
      id: 'seleccionar',
      escena: 'brujula',
      radio: 26,
      texto: t(
        '"Select" lets you pick several transactions at once. Tip: you can also get there by holding your finger on one.',
      ),
      objetivo: () => uno('[data-testid="opcion-seleccionar"]'),
    },
    {
      id: 'barra',
      escena: 'seleccion',
      disponible: hayDosElegibles,
      texto: t(
        'With several picked, "Edit" changes them all at once and ⋮ has Duplicate, Delete, Merge and Make transfer. The ✕ gets you out of selecting.',
      ),
      objetivo: () => uno('[data-testid="barra-seleccion"]'),
    },
    {
      id: 'editar',
      escena: 'editar',
      radio: 22,
      disponible: hayDosElegibles,
      texto: t(
        'Choose what to change (account, shop, notes, category or confirmed) and it applies to all the ones you picked.',
      ),
      objetivo: () => uno('[data-testid="hoja-editar-campos"]'),
    },
    {
      id: 'anadir',
      escena: 'brujula',
      radio: 26,
      texto: t(
        'Back to the compass. "Add transaction" notes an expense or income by hand. You have it on every screen.',
      ),
      objetivo: () => uno('[data-testid="opcion-anadir-movimiento"]'),
    },
    {
      id: 'mas',
      escena: 'brujula',
      radio: 26,
      texto: t('"More" takes you to Settings and the rest of Rumbo.'),
      objetivo: () => uno('[data-testid="opcion-ajustes"]'),
    },
    {
      id: 'final',
      escena: 'normal',
      radio: 30,
      final: true,
      texto: t(
        'Done, now you know your way around here! If you forget something, tap the porthole and I will tell you again.',
      ),
      objetivo: () => uno('[data-testid="ojo-de-buey"]'),
    },
  ];
  const paso = (id: string) => todo.filter(p => p.id === id);
  const anadir = paso('anadir').map(p => ({
    ...p,
    texto: t(
      '"Add transaction" notes an expense or income by hand. You have it on every screen.',
    ),
  }));

  return [
    { id: 'todo', texto: t('Tell me about this screen'), pasos: todo },
    {
      id: 'buscar',
      texto: t('How do I find a transaction?'),
      pasos: paso('buscar'),
    },
    {
      id: 'varios',
      texto: t('How do I change several at once?'),
      pasos: ['brujula', 'seleccionar', 'barra', 'editar'].flatMap(paso),
    },
    {
      id: 'sin-categoria',
      texto: t('What do I do with "Uncategorized"?'),
      pasos: paso('sin-categoria'),
    },
    {
      id: 'apuntar',
      texto: t('How do I note an expense by hand?'),
      pasos: [...paso('brujula'), ...anadir],
    },
  ];
}
